import { ReactNode } from 'react';

import {
  Box,
  FormControlLabel,
  FormControlLabelProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import { Radio, RadioProps } from './Radio';
import { Sizes, WithRef } from '../../../types';
import { mergeSx } from '../../../utils';
import { tokens } from '../../../theme/generated/tokens';

type LabeledRadioSizes = Sizes<'xs' | 'sm' | 'md'>;

type LabeledRadioProps = {
  size?: LabeledRadioSizes;
  radioProps?: RadioProps;
  formControlLabelProps?: FormControlLabelProps;
  label: ReactNode;
  labelProps?: TypographyProps;
  description?: ReactNode;
  descriptionProps?: TypographyProps;
  className?: string;
};

const sizeToRadioSize: Record<LabeledRadioSizes, RadioProps['size']> = {
  xs: 'small',
  sm: 'small',
  md: 'medium',
};

const sizeToDescriptionVariant: Record<LabeledRadioSizes, string> = {
  xs: 'P10R',
  sm: 'P12R',
  md: 'P14R',
};

const sizeToLabelVariant: Record<LabeledRadioSizes, string> = {
  xs: 'P10M',
  sm: 'P12M',
  md: 'P14M',
};

/** @deprecated `LabeledRadio` is deprecated — use `ControlLabel` with a `Radio` control instead. */
export const LabeledRadio = ({
  ref,
  radioProps,
  formControlLabelProps,
  label,
  labelProps,
  description,
  descriptionProps,
  size = 'sm',
  className,
}: WithRef<HTMLDivElement, LabeledRadioProps>) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content auto',
        gridTemplateRows: 'max-content auto',
        alignItems: 'flex-start',
        gridColumnGap: 8,
        gridRowGap: size !== 'xs' ? 2 : undefined,
      }}
      className={className}
      ref={ref}
    >
      <FormControlLabel
        control={
          <Radio
            size={sizeToRadioSize[size]}
            {...radioProps}
            sx={mergeSx({ alignSelf: 'center' }, radioProps?.sx)}
          />
        }
        label={
          <Typography variant={sizeToLabelVariant[size] as any} {...labelProps}>
            {label}
          </Typography>
        }
        {...formControlLabelProps}
        sx={mergeSx({ display: 'contents' }, formControlLabelProps?.sx)}
      />

      {!!description && (
        <Typography
          variant={sizeToDescriptionVariant[size] as any}
          gridColumn={2}
          sx={{ color: tokens.theme.fg.structural.secondary }}
          {...descriptionProps}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

LabeledRadio.displayName = 'LabeledRadio';
